import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiX, FiKey, FiUsers, FiLoader, FiCheck, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { joinGroupByInviteCode } from '../services/groupService';
import { hapticSuccess } from '../services/hapticsService';
import PaywallModal from './PaywallModal';

function JoinGroupModal({ isOpen, onClose, onJoinSuccess }) {
  const navigate = useNavigate();
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState(null);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const {data, error} = await joinGroupByInviteCode(inviteCode.trim());
      
      if (error) {
        if (error.code === 'PLAN_CAP_GROUPS') {
          setPaywallFeature('groups');
        } else if (error.code === 'PLAN_CAP_GROUP_SIZE') {
          setPaywallFeature('group_size');
        } else {
          setError(error.message || 'Failed to join group');
        }
        setLoading(false);
        return;
      }

      setSuccess(`Successfully joined "${data.group.name}"!`);
      hapticSuccess();

      // Call success callback to refresh groups
      if (onJoinSuccess) {
        await onJoinSuccess();
      }

      // Close modal and redirect after a short delay
      setTimeout(() => {
        onClose();
        setInviteCode('');
        setError('');
        setSuccess('');
        navigate('/');
      }, 2000);

    } catch (err) {
      setError('Failed to join group. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setInviteCode('');
      setError('');
      setSuccess('');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: isMobile ? 50 : 0 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: isMobile ? 50 : 0 }}
          className={`bg-surface border border-border-subtle shadow-modal w-full ${
            isMobile
              ? 'rounded-t-2xl max-h-[95vh] fixed bottom-0 left-0 right-0 overflow-y-auto'
              : 'rounded-lg max-w-md mx-auto'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={isMobile ? 'p-6 pb-24' : 'p-6'}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <div className="flex-1 min-w-0">
                <h2 className="text-h3 text-text-primary mb-2 truncate">Join Golf Group</h2>
                <p className="text-body text-text-secondary">Enter an invite code to join</p>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="w-11 h-11 rounded-xl flex items-center justify-center hover:bg-card transition-colors duration-200 disabled:opacity-50 ml-4 flex-shrink-0"
                aria-label="Close join group modal"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <motion.div
                  initial={{opacity: 0, x: -20}}
                  animate={{opacity: 1, x: 0}}
                  className="bg-status-error/10 border border-status-error/30 text-status-error px-4 py-3 rounded-lg text-sm font-medium flex items-center"
                >
                  <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2 flex-shrink-0" />
                  {error}
                </motion.div>
              )}

              {success && (
                <motion.div
                  initial={{opacity: 0, x: -20}}
                  animate={{opacity: 1, x: 0}}
                  className="bg-accent/10 border border-accent text-accent px-4 py-3 rounded-lg text-sm font-medium flex items-center"
                >
                  <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2 flex-shrink-0" />
                  {success}
                </motion.div>
              )}

              <div>
                <label htmlFor="inviteCode" className="block text-sm font-semibold text-text-secondary mb-3">
                  Invite Code
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-muted">
                    <SafeIcon icon={FiKey} className="w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    id="inviteCode"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    disabled={loading || success}
                    className={`w-full pl-12 pr-4 border border-border-subtle rounded-lg bg-surface text-text-primary placeholder-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors font-mono text-center font-medium disabled:bg-app disabled:opacity-60 min-h-[44px] ${
                      isMobile ? 'py-3 text-base' : 'py-4 text-lg'
                    }`}
                    placeholder="Enter 8-character code"
                    maxLength={8}
                    required
                  />
                </div>
              </div>

              <div className={`bg-app border border-border-subtle rounded-lg ${
                isMobile ? 'p-3' : 'p-4'
              }`}>
                <h4 className="font-semibold text-text-primary mb-2 text-sm">Need an invite code?</h4>
                <ul className="text-text-muted space-y-1 text-xs">
                  <li>• Ask a group member to share their invite code</li>
                  <li>• Codes are 8 characters long (letters and numbers)</li>
                </ul>
              </div>

              <div className={`flex pt-2 ${
                isMobile ? 'flex-col space-y-3' : 'space-x-4'
              }`}>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className={`inline-flex items-center justify-center px-5 py-2.5 border border-border-subtle text-text-secondary rounded-lg hover:bg-surface transition-colors font-medium text-sm disabled:opacity-50 min-h-[44px] ${
                    isMobile ? 'w-full' : 'flex-1'
                  }`}
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={!loading && !success ? { scale: 1.02 } : {}}
                  whileTap={!loading && !success ? { scale: 0.98 } : {}}
                  type="submit"
                  disabled={loading || success}
                  className={`inline-flex items-center justify-center px-5 py-2.5 bg-accent text-white rounded-lg hover:bg-interactive-hover transition-colors font-medium text-sm disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px] ${
                    isMobile ? 'w-full' : 'flex-1'
                  }`}
                >
                  {loading ? (
                    <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                  ) : success ? (
                    <>
                      <SafeIcon icon={FiCheck} className="w-5 h-5 mr-2" />
                      Joined!
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiUsers} className="w-5 h-5 mr-2" />
                      Join Group
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
      <PaywallModal
        isOpen={!!paywallFeature}
        onClose={() => setPaywallFeature(null)}
        feature={paywallFeature}
      />
    </AnimatePresence>
  );
}

export default JoinGroupModal;